import * as React from 'react';

import Image from 'lib/image-container';
import { SupportedLanguage } from 'lib/lexemes/supported-languages';
import LocaleContext from 'lib/locale-context';
import { useDetectPrint } from 'lib/use-detect-print';
import useMediaQuery, { MediaQueryWidth } from 'lib/use-media-query';
import useScrollbarWidth from 'lib/use-scrollbar-width';

import { Columns } from 'components/columns';
import ColumnsItem, { ColumnsSize } from 'components/columns/columns-item';
import { CtaBox, CtaBoxContent, CtaBoxHeader, CtaBoxText } from 'components/cta-box';
import GoodToKnow from 'components/good-to-know/good-to-know';
import PageSection from 'components/page/page-section';
import {
  COLUMNS_GAP_MEDIUM,
  IMAGE_MAX_WIDTH_1,
  PAGE_PADDING_LARGE,
  PAGE_PADDING_MEDIUM,
  PAGE_PADDING_SMALL,
} from 'components/page/sections';
import PageSectionCta from 'components/page/sections/page-section-cta';
import PageSectionCtaWithoutImage from 'components/page/sections/page-section-cta-without-image';
import SliderBox from 'components/slider-box/slider-box';
import Table from 'components/table/table';
import { FormattedText, P2 } from 'components/typography';

import { getPropsSectionTwoContainer } from './section-two-container.props';

export const SectionTwoContainer: React.FC = () => {
  const lang = React.useContext(LocaleContext);

  const props = getPropsSectionTwoContainer({ lang } as {
    lang: SupportedLanguage;
  });
  const scrollbarWidth = useScrollbarWidth(true);
  const isBreakpoint = useMediaQuery(MediaQueryWidth['desktop-md']);
  const isPrinting = useDetectPrint();
  return (
    <>
      <PageSectionCtaWithoutImage
        titleElement={props.sections.one.titleElement}
        text={props.sections.one.text}
        imageText={
          <GoodToKnow
            color="primary"
            title={props.sections.one.goodToKnow.title}
            contentHtml={
              <FormattedText>
                <P2 html={props.sections.one.goodToKnow.content} as="p" />
              </FormattedText>
            }
          />
        }
        columnLeft={{ size: ColumnsSize['1/3'] }}
        columnRight={{ size: ColumnsSize['2/3'] }}
        color="transparent"
      />

      <PageSection color="peach" extraClass="section-cta" padding="block" inner={true}>
        <Columns hasMobileLayout={true}>
          <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/2']}>
            <CtaBox align={'start'}>
              <CtaBoxContent width="stretch">
                {isPrinting ? null : (
                  <div className="page-section__picture">
                    <Image
                      width={600}
                      height={620}
                      style={{
                        objectFit: 'contain',
                        objectPosition: props.sections.two.imageObjectPosition,
                      }}
                      quality={75}
                      src={props.sections.two.imageSrc}
                      alt=""
                      sizes={`
                    (max-width: ${MediaQueryWidth['desktop-sm']}px) calc(100vw - 2 * ${PAGE_PADDING_SMALL} - ${scrollbarWidth}),
                    (max-width: ${MediaQueryWidth['tablet-lg']}px) calc((100vw - 2 * ${PAGE_PADDING_MEDIUM} - ${COLUMNS_GAP_MEDIUM} - ${scrollbarWidth}) / 2),
                    (max-width: ${MediaQueryWidth['content-max']}px) calc((100vw - 2 * ${PAGE_PADDING_LARGE} - ${COLUMNS_GAP_MEDIUM} - ${scrollbarWidth}) / 2),
                    ${IMAGE_MAX_WIDTH_1}
                `}
                    />
                  </div>
                )}
                <div className="speech-box speech-box--primary speech-box--over-next-bottom speech-box--over-next-bottom-rotated">
                  {props.sections.two.tip}
                </div>
              </CtaBoxContent>
            </CtaBox>
          </ColumnsItem>
          <ColumnsItem size={ColumnsSize['1/2']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.two.titleElement}</CtaBoxHeader>
              <CtaBoxText>{props.sections.two.text}</CtaBoxText>
            </CtaBox>
          </ColumnsItem>
        </Columns>
      </PageSection>

      <PageSectionCta
        titleElement={props.sections.three.titleElement}
        text={
          <CtaBox align="start">
            <CtaBoxText>{props.sections.three.text}</CtaBoxText>
          </CtaBox>
        }
        image={
          isPrinting
            ? undefined
            : {
                //
                src: props.sections.three.imageSrc,
                objectPosition: props.sections.one.imageObjectPosition,
                objectFit: 'contain',
              }
        }
        imageText={
          <>
            <br />
            <SliderBox
              title={props.sections.three.imageTipTitle}
              content={props.sections.three.imageTipContent}
              align="center"
              color="orange"
              showSlider={false}
            />
          </>
        }
        imageTextPosition="bottom"
        columnLeft={{ size: ColumnsSize['1/3'] }}
        columnRight={{ size: ColumnsSize['2/3'] }}
        color="transparent"
      />

      <PageSection color="peach" extraClass="section-cta" padding="block" inner={true}>
        <Columns hasMobileLayout={true}>
          <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/3']}>
            <GoodToKnow
              title={props.sections.four.goodToKnow.title}
              contentHtml={
                <FormattedText>
                  <P2 html={props.sections.four.goodToKnow.content} as="p" />
                </FormattedText>
              }
            />
          </ColumnsItem>

          <ColumnsItem size={ColumnsSize['2/3']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.four.titleElement}</CtaBoxHeader>
              <CtaBoxText>{props.sections.four.text}</CtaBoxText>
              <br />
              <div style={{ display: 'flex' }}>
                <div
                  style={
                    isPrinting
                      ? undefined
                      : {
                          marginLeft: 'auto',
                          marginRight: isBreakpoint ? 0 : '2.5vw',
                        }
                  }
                >
                  <SliderBox
                    title={props.sections.four.ccpa.title}
                    content={props.sections.four.ccpa.content}
                    align="center"
                    color="orange"
                    showSlider={false}
                  />
                </div>
              </div>
            </CtaBox>
          </ColumnsItem>
        </Columns>
      </PageSection>

      <PageSection padding="block" inner={true}>
        <FormattedText>
          <Table rows={props.sections.five.table} />
        </FormattedText>
      </PageSection>

      <PageSection padding="block" extraClass="section-cta" inner={true}>
        <Columns hasMobileLayout={true}>
          <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/1']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.five.titleElement}</CtaBoxHeader>
              <CtaBoxText>
                <FormattedText>
                  <P2 as="p" html={props.sections.five.text1} />
                  <P2 as="p" html={props.sections.five.text2} />
                  <P2 as="p" html={props.sections.five.text3} />
                  <P2 as="p" html={props.sections.five.text4} />
                  <P2 as="p" html={props.sections.five.text5} />
                </FormattedText>
              </CtaBoxText>
            </CtaBox>
          </ColumnsItem>
        </Columns>
      </PageSection>

      <PageSection padding="block" extraClass="section-cta" inner={true}>
        <Columns hasMobileLayout={true}>
          <ColumnsItem verticalAlign="stretch" size={ColumnsSize['1/1']}>
            <CtaBox align="start">
              <CtaBoxHeader tag="h2">{props.sections.six.titleElement}</CtaBoxHeader>
              <CtaBoxText>
                <FormattedText>
                  <P2 as="p" html={props.sections.six.text1} />
                  <P2 as="p" html={props.sections.six.text2} />
                  {/* text3 is a `<ul>` list, so it needs a block-level wrapper */}
                  <P2 as="div" html={props.sections.six.text3} />
                  <P2 as="p" html={props.sections.six.text4} />
                  <P2 as="p" html={props.sections.six.text5} />
                </FormattedText>
              </CtaBoxText>
            </CtaBox>
          </ColumnsItem>
        </Columns>
      </PageSection>
    </>
  );
};
